import {
  appStoreClickResponseSchema,
  cityProgressSchema,
  inviteDetailSchema,
  livePulseSchema,
  worldSnapshotSchema,
  type CityProgressDTO,
  type InviteDetailDTO,
  type LivePulseDTO,
  type WorldSnapshotDTO
} from "@light-earth/contracts";
import { z } from "zod";

const baseURL = import.meta.env.VITE_API_BASE_URL ?? "/api";

export async function fetchWorld(): Promise<WorldSnapshotDTO> {
  const response = await fetch(`${baseURL}/v1/world`);
  if (!response.ok) throw new Error(`world_${response.status}`);
  return worldSnapshotSchema.parse(await response.json());
}

export async function fetchCities(): Promise<CityProgressDTO[]> {
  const response = await fetch(`${baseURL}/v1/cities`);
  if (!response.ok) throw new Error(`cities_${response.status}`);
  return z.object({ cities: z.array(cityProgressSchema) }).parse(await response.json()).cities;
}

export async function fetchInvite(code: string): Promise<InviteDetailDTO> {
  const response = await fetch(
    `${baseURL}/v1/invites/${encodeURIComponent(code)}`
  );
  if (!response.ok) throw new Error(`invite_${response.status}`);
  return inviteDetailSchema.parse(await response.json());
}

export async function recordInviteClick(code: string): Promise<void> {
  const response = await fetch(
    `${baseURL}/v1/invites/${encodeURIComponent(code)}/click`,
    { method: "POST" }
  );
  if (!response.ok && response.status !== 404) {
    throw new Error(`invite_click_${response.status}`);
  }
}

export async function recordAppStoreClick(inviteCode?: string): Promise<void> {
  const response = await fetch(`${baseURL}/v1/app-store-clicks`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      campaignId: "earth-relay-72h",
      ...(inviteCode ? { inviteCode } : {})
    }),
    keepalive: true
  });
  if (response.status === 409) return;
  if (!response.ok) throw new Error(`app_store_click_${response.status}`);
  appStoreClickResponseSchema.parse(await response.json());
}

export function subscribeToWorld(callbacks: {
  onSnapshot: (snapshot: WorldSnapshotDTO) => void;
  onPulse: (pulse: LivePulseDTO) => void;
  onStatus: (connected: boolean) => void;
}): () => void {
  const source = new EventSource(`${baseURL}/v1/live`);
  source.addEventListener("open", () => callbacks.onStatus(true));
  source.addEventListener("error", () => callbacks.onStatus(false));
  source.addEventListener("snapshot", (event) => {
    const result = worldSnapshotSchema.safeParse(JSON.parse((event as MessageEvent).data));
    if (result.success) callbacks.onSnapshot(result.data);
  });
  source.addEventListener("pulse", (event) => {
    const result = livePulseSchema.safeParse(JSON.parse((event as MessageEvent).data));
    if (result.success) callbacks.onPulse(result.data);
  });
  return () => source.close();
}
